/**
 * ComboBox.jsx
 * ------------
 * Custom-dropdown hybrid input: shows a list of known values but also
 * accepts free text entry. Replaces the earlier <datalist>-backed version —
 * the browser's native datalist is unreliable on Safari iOS (Bob's primary
 * device) and can't be programmatically opened, so we render the list
 * ourselves.
 *
 * Public API — unchanged from the datalist version; no caller changes needed.
 *   value       {string}   — current field value
 *   onChange    {fn}       — called with new string value on every change
 *                            (free-text keystrokes AND option selections)
 *   options     {string[]} — suggestions shown in the dropdown
 *   placeholder {string}   — input placeholder text
 *   disabled    {boolean}
 *   className   {string}   — applied to the <input> (not the wrapper)
 *
 * Behaviour:
 *   - Focus input with empty value    → dropdown opens showing ALL options
 *   - Focus input with typed value    → dropdown opens; filtered by substring
 *   - Click chevron                   → toggles dropdown open/closed
 *   - Click option / press Enter on   → commits that option, closes dropdown
 *     a keyboard-highlighted option
 *   - Type free text + blur / Escape  → free text kept, dropdown closes
 *   - Outside click / Escape          → closes dropdown
 *   - ArrowDown / ArrowUp             → moves highlight within filtered list
 *
 * Filtering: case-insensitive substring match. Empty value = show all.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './ComboBox.css';

let _listSeq = 0;
function nextListId() { return `cbx-list-${++_listSeq}`; }

export function ComboBox({
  value,
  onChange,
  options = [],
  placeholder = '',
  disabled = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listId = useRef(nextListId()).current;

  const filtered = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (!q) return options;
    return options.filter(opt => (opt || '').toLowerCase().includes(q));
  }, [options, value]);

  // Reset highlight whenever the filtered set changes so keyboard nav starts
  // from the top of the new list.
  useEffect(() => { setHighlightIdx(-1); }, [filtered]);

  // Outside-click closes the dropdown. Only installed while open.
  useEffect(() => {
    if (!open) return;
    function handleDocMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, [open]);

  // Scroll the highlighted option into view during keyboard nav.
  useEffect(() => {
    if (highlightIdx < 0 || !listRef.current) return;
    const el = listRef.current.children[highlightIdx];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const commit = useCallback((val) => {
    onChange(val);
    setOpen(false);
    setHighlightIdx(-1);
  }, [onChange]);

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation();
        setOpen(false);
        setHighlightIdx(-1);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlightIdx(i => Math.min(filtered.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return;
      setHighlightIdx(i => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      if (open && highlightIdx >= 0 && filtered[highlightIdx] != null) {
        e.preventDefault();
        commit(filtered[highlightIdx]);
      }
      // else: let the Enter bubble — some forms use it to submit. Don't trap.
      return;
    }
  }

  // preventDefault on mousedown inside the menu stops the input from losing
  // focus when clicking an option. Without it, input blurs before the option's
  // onClick fires, the dropdown unmounts, and the click never lands.
  function handleMenuMouseDown(e) {
    e.preventDefault();
  }

  return (
    <div className="cbx-wrap" ref={wrapRef}>
      <input
        ref={inputRef}
        type="text"
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listId}
      />
      <button
        type="button"
        className={`cbx-chevron ${open ? 'open' : ''}`}
        onMouseDown={(e) => {
          // Prevent the input from blurring on chevron click so the open
          // state we set immediately after isn't clobbered by the blur race.
          e.preventDefault();
        }}
        onClick={() => {
          if (disabled) return;
          setOpen(prev => !prev);
          inputRef.current?.focus();
        }}
        aria-label={open ? 'Close options' : 'Open options'}
        tabIndex={-1}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          className="cbx-menu"
          role="listbox"
          onMouseDown={handleMenuMouseDown}
        >
          {filtered.length === 0 ? (
            <li className="cbx-option-empty">No matches</li>
          ) : (
            filtered.map((opt, idx) => (
              <li
                key={opt}
                className={`cbx-option ${idx === highlightIdx ? 'highlighted' : ''}`}
                role="option"
                aria-selected={idx === highlightIdx}
                onClick={() => commit(opt)}
                onMouseEnter={() => setHighlightIdx(idx)}
              >
                {opt}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
